import { type DBCResponse, dbcApi } from '@/api/dbcApi'
import type { DependencyObject } from '@/api/deviceApi'
import { type Exporter, exportersApi } from '@/api/exportersApi'
import { type SessionRule, sessionsApi } from '@/api/sessionsApi'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
	ensureDashboardSummaryList,
	readDashboardSummaryListCache,
	useDashboardSummaries,
} from '@/hooks/queries/dashboards/useDashboardList'
import { asList } from '@/utils/asList'
import { Activity, Cpu, LayoutDashboard, Server, ShieldAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'

interface DependencyWarningProps {
	dependencies: DependencyObject | undefined | null
	actionName: string // e.g. "delete" or "edit"
	entityName: string // e.g. "stream", "metadata key", "column"
	entityLabel: string // e.g. "my_stream", "city", "temperature"
	dbcParserNames?: string[]
	/** Replaces the "Cannot {actionName}…" line on surfaces where the action is allowed and the list is informational. */
	heading?: string
}

interface ResolvedDependencyLists {
	dashboards: string[]
	exporters: string[]
	sessionRules: string[]
	dbcParsers: string[]
	deviceStates: string[]
}

interface DependencyWarningTableProps {
	entries: Array<{ key: string; dependencies: DependencyObject | undefined | null }>
	keyColumnLabel?: string
}

export function hasDependencies(dependency: DependencyObject | undefined | null, dbcParserNames?: string[]): boolean {
	if (dbcParserNames && dbcParserNames.length > 0) {
		return true
	}
	if (!dependency) {
		return false
	}
	return (
		(dependency.dashboards?.length ?? 0) > 0 ||
		(dependency.exporters?.length ?? 0) > 0 ||
		(dependency.session_rules?.length ?? 0) > 0 ||
		(dependency.dbc_parser?.length ?? 0) > 0 ||
		(dependency.device_state?.length ?? 0) > 0
	)
}

export function getDependencies(_name: string, apiDependency?: DependencyObject | null): DependencyObject {
	if (apiDependency && hasDependencies(apiDependency)) {
		return {
			dashboards: asList(apiDependency.dashboards),
			exporters: asList(apiDependency.exporters),
			session_rules: asList(apiDependency.session_rules),
			dbc_parser: asList(apiDependency.dbc_parser),
			device_state: asList(apiDependency.device_state),
		}
	}

	return { dashboards: [], exporters: [], session_rules: [], dbc_parser: [], device_state: [] }
}

export async function resolveDependencyListsAsync(
	dependencies: DependencyObject | undefined | null,
	dbcParserNames?: string[],
): Promise<ResolvedDependencyLists> {
	if (!hasDependencies(dependencies, dbcParserNames)) {
		return { dashboards: [], exporters: [], sessionRules: [], dbcParsers: [], deviceStates: [] }
	}

	const rawDashboards = (Array.isArray(dependencies?.dashboards) ? dependencies.dashboards : []).map(String)
	const rawExporters = (Array.isArray(dependencies?.exporters) ? dependencies.exporters : []).map(String)
	const rawSessionRules = (Array.isArray(dependencies?.session_rules) ? dependencies.session_rules : []).map(String)
	const rawDbcParsers = (Array.isArray(dependencies?.dbc_parser) ? dependencies.dbc_parser : []).map(String)
	const rawDeviceStates = (Array.isArray(dependencies?.device_state) ? dependencies.device_state : []).map(String)

	const fetchPromises: Promise<unknown>[] = []

	let currentDashboards = readDashboardSummaryListCache() ?? []
	if (rawDashboards.length > 0 && currentDashboards.length === 0) {
		fetchPromises.push(
			ensureDashboardSummaryList()
				.then(fetched => {
					currentDashboards = fetched
				})
				.catch(err => console.error('Failed to fetch dashboards for warning resolving:', err)),
		)
	}

	let fetchedExporters: Exporter[] = []
	if (rawExporters.length > 0) {
		fetchPromises.push(
			exportersApi
				.getExporters(1, 1000)
				.then(res => {
					fetchedExporters = asList(res.data)
				})
				.catch(err => console.error('Failed to fetch exporters for warning resolving:', err)),
		)
	}

	let fetchedDbcs: DBCResponse[] = []
	if (rawDbcParsers.length > 0 || (dbcParserNames && dbcParserNames.length > 0)) {
		fetchPromises.push(
			dbcApi
				.getDBCLists({ page: 1, per_page: 1000 })
				.then(res => {
					fetchedDbcs = asList(res.data)
				})
				.catch(err => console.error('Failed to fetch DBC parsers for warning resolving:', err)),
		)
	}

	let fetchedRules: SessionRule[] = []
	if (rawSessionRules.length > 0) {
		fetchPromises.push(
			sessionsApi
				.getSessionRules({ page: 1, per_page: 1000 })
				.then(res => {
					const rules = 'data' in res ? res.data : (res as unknown as SessionRule[])
					fetchedRules = asList(rules)
				})
				.catch(err => console.error('Failed to fetch session rules for warning resolving:', err)),
		)
	}

	await Promise.all(fetchPromises)

	const resolveDashboardName = (val: string) => {
		const parsedId = Number(val)
		if (!Number.isNaN(parsedId)) {
			const found = currentDashboards.find(d => d.id === parsedId)
			if (found) {
				return found.title || `Dashboard (ID: ${val})`
			}
		}
		const foundByStringId = currentDashboards.find(d => String(d.id) === val)
		if (foundByStringId) {
			return foundByStringId.title || `Dashboard (ID: ${val})`
		}
		const foundByTitle = currentDashboards.find(d => d.title === val)
		if (foundByTitle) {
			return foundByTitle.title
		}
		return val
	}

	const resolveResourceName = <T extends { id: string | number; name: string }>(list: T[], val: string): string => {
		const found = list.find(item => String(item.id) === val || item.name === val)
		return found ? found.name : val
	}

	const resolveAndDeduplicate = (items: string[], resolver: (val: string) => string): string[] => {
		return Array.from(new Set(items.map(resolver)))
	}

	const allDbcParsers = [...rawDbcParsers]
	if (dbcParserNames) {
		for (const pName of dbcParserNames) {
			if (!allDbcParsers.includes(pName)) {
				allDbcParsers.push(pName)
			}
		}
	}

	return {
		dashboards: resolveAndDeduplicate(rawDashboards, resolveDashboardName),
		exporters: resolveAndDeduplicate(rawExporters, e => resolveResourceName(fetchedExporters, e)),
		sessionRules: resolveAndDeduplicate(rawSessionRules, s => resolveResourceName(fetchedRules, s)),
		dbcParsers: resolveAndDeduplicate(allDbcParsers, p => resolveResourceName(fetchedDbcs, p)),
		deviceStates: Array.from(new Set(rawDeviceStates)),
	}
}

/** One dependency category per line, so a toast/error surface can render them as a readable list. */
export function formatDependencySummary(resolved: ResolvedDependencyLists): string {
	const parts: string[] = []
	if (resolved.dashboards.length > 0) {
		parts.push(`Dashboards: ${resolved.dashboards.join(', ')}`)
	}
	if (resolved.sessionRules.length > 0) {
		parts.push(`Session Rules: ${resolved.sessionRules.join(', ')}`)
	}
	if (resolved.exporters.length > 0) {
		parts.push(`Exporters: ${resolved.exporters.join(', ')}`)
	}
	if (resolved.dbcParsers.length > 0) {
		parts.push(`DBC Parsers: ${resolved.dbcParsers.join(', ')}`)
	}
	if (resolved.deviceStates.length > 0) {
		parts.push(`Device State: ${resolved.deviceStates.join(', ')}`)
	}
	return parts.join('\n')
}

/** Backend reason on the first line, then one line per dependency category. */
export function buildErrorMessageWithDependencies(baseMessage: string, resolved: ResolvedDependencyLists): string {
	const summary = formatDependencySummary(resolved)
	if (!summary) {
		return baseMessage
	}
	// Strip any trailing punctuation so the appended colon never doubles up ("modified.:").
	const cleanBase = baseMessage.trim().replace(/[.:;,]+$/, '')
	return `${cleanBase}:\n${summary}`
}

// Fetches dashboards/exporters/session rules and resolves dependency ids to display names.
function useDependencyResolvers(): {
	resolveDependencyLists: (
		dependencies: DependencyObject | undefined | null,
		dbcParserNames?: string[],
	) => ResolvedDependencyLists
} {
	const { dashboards } = useDashboardSummaries()
	const [exporters, setExporters] = useState<Exporter[]>([])
	const [dbcs, setDbcs] = useState<DBCResponse[]>([])
	const [allRules, setAllRules] = useState<SessionRule[]>([])

	useEffect(() => {
		// Fetched fresh into local state (not the shared, paginated exporters/dbc stores) so a
		// partial page cached by another view can't cause names to fall back to raw ids here.
		exportersApi
			.getExporters(1, 1000)
			.then(res => setExporters(asList(res.data)))
			.catch(err => console.error('Failed to fetch exporters for warning resolving:', err))

		dbcApi
			.getDBCLists({ page: 1, per_page: 1000 })
			.then(res => setDbcs(asList(res.data)))
			.catch(err => console.error('Failed to fetch DBC parsers for warning resolving:', err))

		sessionsApi
			.getSessionRules({ page: 1, per_page: 1000 })
			.then(res => {
				const rules = 'data' in res ? res.data : (res as unknown as SessionRule[])
				setAllRules(asList(rules))
			})
			.catch(err => console.error('Failed to fetch session rules locally for warning resolving:', err))
	}, [])

	const resolveDashboardName = (val: string) => {
		const parsedId = Number(val)
		if (!Number.isNaN(parsedId)) {
			const found = dashboards.find(d => d.id === parsedId)
			if (found) {
				return found.title || `Dashboard (ID: ${val})`
			}
		}
		const foundByStringId = dashboards.find(d => String(d.id) === val)
		if (foundByStringId) {
			return foundByStringId.title || `Dashboard (ID: ${val})`
		}
		const foundByTitle = dashboards.find(d => d.title === val)
		if (foundByTitle) {
			return foundByTitle.title
		}
		return val
	}

	const resolveResourceName = <T extends { id: string | number; name: string }>(list: T[], val: string): string => {
		const found = list.find(item => String(item.id) === val || item.name === val)
		return found ? found.name : val
	}

	const resolveAndDeduplicate = (items: string[], resolver: (val: string) => string): string[] => {
		return Array.from(new Set(items.map(resolver)))
	}

	const resolveDependencyLists = (
		dependencies: DependencyObject | undefined | null,
		dbcParserNames?: string[],
	): ResolvedDependencyLists => {
		const normalized = {
			dashboards: [] as string[],
			exporters: [] as string[],
			sessionRules: [] as string[],
			dbcParsers: [] as string[],
			deviceStates: [] as string[],
		}

		if (dependencies) {
			normalized.dashboards = Array.isArray(dependencies.dashboards) ? dependencies.dashboards.map(String) : []
			normalized.exporters = Array.isArray(dependencies.exporters) ? dependencies.exporters.map(String) : []
			normalized.sessionRules = Array.isArray(dependencies.session_rules) ? dependencies.session_rules.map(String) : []
			normalized.dbcParsers = Array.isArray(dependencies.dbc_parser) ? dependencies.dbc_parser.map(String) : []
			normalized.deviceStates = Array.isArray(dependencies.device_state) ? dependencies.device_state.map(String) : []
		}

		if (dbcParserNames && dbcParserNames.length > 0) {
			for (const pName of dbcParserNames) {
				if (!normalized.dbcParsers.includes(pName)) {
					normalized.dbcParsers.push(pName)
				}
			}
		}

		return {
			dashboards: resolveAndDeduplicate(normalized.dashboards, resolveDashboardName),
			exporters: resolveAndDeduplicate(normalized.exporters, e => resolveResourceName(exporters, e)),
			sessionRules: resolveAndDeduplicate(normalized.sessionRules, s => resolveResourceName(allRules, s)),
			dbcParsers: resolveAndDeduplicate(normalized.dbcParsers, p => resolveResourceName(dbcs, p)),
			deviceStates: Array.from(new Set(normalized.deviceStates)),
		}
	}

	return { resolveDependencyLists }
}

export function DependencyWarning({
	dependencies,
	actionName,
	entityName,
	entityLabel,
	dbcParserNames,
	heading,
}: DependencyWarningProps): JSX.Element | null {
	const { resolveDependencyLists } = useDependencyResolvers()

	if (!hasDependencies(dependencies, dbcParserNames)) {
		return null
	}

	const {
		dashboards: resolvedDashboards,
		exporters: resolvedExporters,
		sessionRules: resolvedSessionRules,
		dbcParsers: resolvedDbcParsers,
		deviceStates: resolvedDeviceStates,
	} = resolveDependencyLists(dependencies, dbcParserNames)

	return (
		<div className='space-y-4 my-2 text-left' data-testid='dependency-warning'>
			<p className='text-control text-muted-foreground font-semibold'>
				{heading ?? `Cannot ${actionName} ${entityName} "${entityLabel}" due to the following active dependencies:`}
			</p>

			<div className='space-y-3 max-h-[300px] overflow-y-auto pr-1 scrollbar-thin'>
				{resolvedDashboards.length > 0 && (
					<div className='border border-border bg-card p-3 rounded-md space-y-1.5' data-testid='dependency-dashboards'>
						<div className='flex items-center gap-2 text-muted-foreground font-medium text-body'>
							<LayoutDashboard className='size-4 shrink-0' />
							<span>Dashboards ({resolvedDashboards.length})</span>
						</div>
						<ul className='list-disc pl-5 text-control text-foreground space-y-0.5'>
							{resolvedDashboards.map(name => (
								<li key={name}>{name}</li>
							))}
						</ul>
					</div>
				)}

				{resolvedExporters.length > 0 && (
					<div className='border border-border bg-card p-3 rounded-md space-y-1.5' data-testid='dependency-exporters'>
						<div className='flex items-center gap-2 text-muted-foreground font-medium text-body'>
							<Server className='size-4 shrink-0' />
							<span>Exporters ({resolvedExporters.length})</span>
						</div>
						<ul className='list-disc pl-5 text-control text-foreground space-y-0.5'>
							{resolvedExporters.map(name => (
								<li key={name}>{name}</li>
							))}
						</ul>
					</div>
				)}

				{resolvedSessionRules.length > 0 && (
					<div className='border border-border bg-card p-3 rounded-md space-y-1.5' data-testid='dependency-sessions'>
						<div className='flex items-center gap-2 text-muted-foreground font-medium text-body'>
							<ShieldAlert className='size-4 shrink-0' />
							<span>SessionRules ({resolvedSessionRules.length})</span>
						</div>
						<ul className='list-disc pl-5 text-control text-foreground space-y-0.5'>
							{resolvedSessionRules.map(name => (
								<li key={name}>{name}</li>
							))}
						</ul>
					</div>
				)}

				{resolvedDbcParsers.length > 0 && (
					<div className='border border-border bg-card p-3 rounded-md space-y-1.5' data-testid='dependency-dbcparsers'>
						<div className='flex items-center gap-2 text-muted-foreground font-medium text-body'>
							<Cpu className='size-4 shrink-0' />
							<span>DBC Parsers ({resolvedDbcParsers.length})</span>
						</div>
						<ul className='list-disc pl-5 text-control text-foreground space-y-0.5'>
							{resolvedDbcParsers.map(name => (
								<li key={name}>{name}</li>
							))}
						</ul>
					</div>
				)}

				{resolvedDeviceStates.length > 0 && (
					<div className='border border-border bg-card p-3 rounded-md space-y-1.5' data-testid='dependency-devicestate'>
						<div className='flex items-center gap-2 text-muted-foreground font-medium text-body'>
							<Activity className='size-4 shrink-0' />
							<span>Device State ({resolvedDeviceStates.length})</span>
						</div>
						<ul className='list-disc pl-5 text-control text-foreground space-y-0.5'>
							{resolvedDeviceStates.map(name => (
								<li key={name}>{name}</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

/**
 * What holds a resource, as one labelled row per kind. For surfaces that need to name the holders in
 * passing; `DependencyWarningTable` is the one to reach for when each row has its own set. The kind is
 * written out beside its icon, so the grouping does not rest on the icon alone.
 */
export function DependencyUsageList({
	dependencies,
	dbcParserNames,
}: {
	dependencies: DependencyObject | undefined | null
	dbcParserNames?: string[]
}): JSX.Element | null {
	const { resolveDependencyLists } = useDependencyResolvers()
	const resolved = resolveDependencyLists(dependencies, dbcParserNames)

	const groups = [
		{ label: 'Dashboards', Icon: LayoutDashboard, names: resolved.dashboards },
		{ label: 'Session rules', Icon: ShieldAlert, names: resolved.sessionRules },
		{ label: 'Exporters', Icon: Server, names: resolved.exporters },
		{ label: 'DBC parsers', Icon: Cpu, names: resolved.dbcParsers },
		{ label: 'Device state', Icon: Activity, names: resolved.deviceStates },
	].filter(group => group.names.length > 0)

	if (groups.length === 0) {
		return null
	}

	return (
		<div className='flex flex-col gap-1' data-testid='dependency-usage-list'>
			{groups.map(({ label, Icon, names }) => (
				<div key={label} className='flex items-start gap-2 text-body'>
					<span className='inline-flex w-28 shrink-0 items-center gap-1.5 text-control text-muted-foreground'>
						<Icon className='size-(--icon-inline) shrink-0' aria-hidden='true' />
						{label}
					</span>
					<span className='text-foreground'>{names.join(', ')}</span>
				</div>
			))}
		</div>
	)
}

// Per-key breakdown of dependencies, e.g. which dashboards/session rules/exporters reference
// each metadata key in a batch of keys being skipped.
export function DependencyWarningTable({
	entries,
	keyColumnLabel = 'Key',
}: DependencyWarningTableProps): JSX.Element | null {
	const { resolveDependencyLists } = useDependencyResolvers()

	const rows = entries
		.map(entry => ({ key: entry.key, resolved: resolveDependencyLists(entry.dependencies) }))
		.filter(
			row =>
				row.resolved.dashboards.length > 0 ||
				row.resolved.exporters.length > 0 ||
				row.resolved.sessionRules.length > 0 ||
				row.resolved.dbcParsers.length > 0 ||
				row.resolved.deviceStates.length > 0,
		)

	if (rows.length === 0) {
		return null
	}

	return (
		<div className='my-2 text-left' data-testid='dependency-warning-table'>
			<div className='border rounded-[6px]'>
				<Table containerClassName='max-h-[300px] overflow-y-auto'>
					<TableHeader className='sticky top-0 z-10'>
						<TableRow>
							<TableHead>{keyColumnLabel}</TableHead>
							<TableHead>Dashboards</TableHead>
							<TableHead>Session Rules</TableHead>
							<TableHead>Exporters</TableHead>
							<TableHead>DBC Parsers</TableHead>
							<TableHead>Device State</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map(row => (
							<TableRow key={row.key}>
								<TableCell className='font-medium align-top'>{row.key}</TableCell>
								<TableCell className='align-top whitespace-normal break-words max-w-50'>
									{row.resolved.dashboards.join(', ') || '—'}
								</TableCell>
								<TableCell className='align-top whitespace-normal break-words max-w-50'>
									{row.resolved.sessionRules.join(', ') || '—'}
								</TableCell>
								<TableCell className='align-top whitespace-normal break-words max-w-50'>
									{row.resolved.exporters.join(', ') || '—'}
								</TableCell>
								<TableCell className='align-top whitespace-normal break-words max-w-50'>
									{row.resolved.dbcParsers.join(', ') || '—'}
								</TableCell>
								<TableCell className='align-top whitespace-normal break-words max-w-50'>
									{row.resolved.deviceStates.join(', ') || '—'}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	)
}
